import { useJobillaOrgId } from '@/hooks'
import { campaignsIndexQueryKey, campaignsShowQueryKey, useCampaignStatusChange } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import { useState } from 'react'
import { appendToastAtom, CommonCampaignCardViewModel, logger, useToastStoreContext } from '../../ui'
import { useIsJobillan } from '../useIsJobillan'
import { useCampaignData } from './-useCampaignData'
import { useCampaignInvoiceData } from './-useCampaignInvoiceData'
import { useCampaignPreviews } from './-useCampaignPreviews'

export function useCommonCampaignCardProps(campaignId: string, companyId: string): CommonCampaignCardViewModel {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const jobillaOrgId = useJobillaOrgId()
	const toastStore = useToastStoreContext()
	const appendToast = useSetAtom(appendToastAtom, { store: toastStore })
	const { campaign, isLoading: isLoadingCampaign } = useCampaignData({ campaignId })
	const { invoice } = useCampaignInvoiceData(
		jobillaOrgId,
		campaignId,
	)
	const { bakedInFeedAd, bakedInStoryAd, isLoading: isLoadingPreviews } = useCampaignPreviews(campaignId)
	const isJobillan = useIsJobillan()
	const [showPullToDraftConfirmation, setShowPullToDraftConfirmation] = useState(false)
	const { VITE_FORM_URL } = import.meta.env
	if (!VITE_FORM_URL) {
		throw new Error('VITE_FORM_URL is not defined in the environment variables.')
	}

	const campaignStatusChange = useCampaignStatusChange()

	return {
		view: {
			organisation: {
				id: jobillaOrgId as string,
				jobilla_id: jobillaOrgId,
			},
			campaign,
			invoice: invoice?.invoice!,
			bakedInFeedAd,
			bakedInStoryAd,
		},
		permissions: {
			canApprove: isJobillan,
			canConfigureTargeting: isJobillan && campaign.status === 'review',
			canSeeAnalytics: ['completed', 'active', 'review'].includes(campaign.status),
			canSeeInvoice: isJobillan,
		},
		loading: {
			overall: isLoadingCampaign || isLoadingPreviews,
			pullingToDraft: campaignStatusChange.isPending,
		},
		renderers: {},
		events: {
			onClose: () => navigate({ to: '/company/$companyId', params: { companyId }, resetScroll: false }),
			onRequestPullToDraft: () => {
				setShowPullToDraftConfirmation(true)
			},
			onPullToDraft: () => {
				campaignStatusChange.mutate(
					{ campaign: campaignId, data: { status: 'draft' } },
					{
						onSuccess: async () => {
							await queryClient.setQueryData(
								campaignsIndexQueryKey({
									'filter[organisation_id]': jobillaOrgId ?? undefined,
									'filter[is_archived]': 'false',
									sort: ['created_at'],
								}),
								(oldData: any) => {
									if (!oldData) { return oldData }
									const updatedCampaigns = oldData.data.map((campaign: any) => {
										if (campaign.id === campaignId) {
											return {
												...campaign,
												attributes: {
													...campaign.attributes,
													status: 'draft',
												},
											}
										}
										return campaign
									})
									return {
										...oldData,
										data: updatedCampaigns,
									}
								},
							)

							await navigate({ to: '/company/$companyId', params: { companyId }, resetScroll: false })
							void queryClient.invalidateQueries({
								queryKey: campaignsShowQueryKey(campaignId),
							})

							await queryClient.invalidateQueries({
								queryKey: campaignsIndexQueryKey({
									'filter[organisation_id]': jobillaOrgId ?? undefined,
									'filter[is_archived]': 'false',
									sort: ['created_at'],
								}),
							})
						},
						onError: (error) => {
							logger.error(error, {
								tags: { component: 'CampaignCard', action: 'pull-to-draft' },
								data: { campaignId },
							})
							appendToast({
								title: 'Failed to move campaign back to draft',
								description: error instanceof Error
									? error.message
									: 'An unexpected error occurred. Please try again.',
								variant: 'error',
								cause: error,
							})
						},
					},
				)
			},
		},
		state: {
			showPullToDraftConfirmation,
			onClosePullToDraftConfirmation: () => setShowPullToDraftConfirmation(false),
		},
	}
}
